/** A rectangle in map-area coordinates: origin top-left, CSS pixels. */
export type MapRect = { x: number; y: number; width: number; height: number };

/**
 * The control stack's geometry, in CSS pixels (Figma `787:679337`). **This is the source of truth** —
 * `map-control-stack.module.scss` repeats these numbers and says so, because SCSS cannot import
 * TypeScript and generating a token file for four values no other surface uses would cost more than
 * it saves. Change one, change both.
 *
 * `buttonCount` is **3**, not admin's 4: this app ships My location and zoom-in/zoom-out only.
 */
export const MAP_CONTROL_LAYOUT = {
  /** Every control is a 36px square. No `--space-*` step sits at 36. */
  buttonSize: 36,
  /** Between the dark zoom buttons — Figma 6px, off the `--space-*` scale, as the stylesheet records. */
  buttonGap: 6,
  /**
   * **3 since ticket 13**: My location joined the top of the same column. It was left at 2 there,
   * which under-reserved the stack by 49px and let an anchored panel cover the new control (113586
   * §4f) — caught when tickets 11 and 13 were first integrated, and corrected here.
   */
  buttonCount: 3,
  /** Between My location and the zoom pair — `gap: var(--space-3)` in the stylesheet. */
  groupGap: 12,
  /** `bottom: var(--space-8)`. */
  insetBlockEnd: 32,
  /** `inset-inline-end: var(--space-16)`. */
  insetInlineEnd: 64,
} as const;

/**
 * The legend's geometry, in CSS pixels (Figma `787:681184`) — top **inline-end**, sharing the control
 * stack's end inset, which is what lines their right edges up. `map-legend.module.scss` repeats these
 * numbers for the same reason `MAP_CONTROL_LAYOUT` does.
 *
 * `width` is the **widest** case — both pills plus the gap (83 + 8 + 71 in `787:678133`). The legend is
 * presence-driven and may render one pill or none, so this over-reserves. That is deliberate: measuring
 * it would mean threading a live rect through the placement arithmetic to save a panel a few pixels of
 * freedom in one corner, and a Grower with no Boundaries drawn has no Field to focus and so no panel
 * to place.
 */
export const MAP_LEGEND_LAYOUT = {
  width: 162,
  height: 26,
  /** D-01: `top: 106px` — 23px below `<TopNav>`'s own bottom edge on the now full-bleed Map. Off
   *  the `--space-*` scale, as the stylesheet records. */
  insetBlockStart: 106,
  /** `inset-inline-end: var(--space-16)`, matching the control stack. */
  insetInlineEnd: 64,
} as const;

/**
 * The search's geometry, in CSS pixels (Figma `787:678207` — the input plus its open panel) — top
 * **inline-start**, mirroring the legend's inset so the two top corners line up. `map-search.module.scss`
 * repeats these numbers for the same reason the other two layouts do: SCSS cannot import TS.
 *
 * `height` is the **open** case and deliberately over-reserves: the panel constrains its own height and
 * scrolls internally, so this is its ceiling rather than its measurement. A Grower with the panel shut
 * loses a little placement freedom in one corner, which costs nothing — measuring it would mean
 * threading a live rect through the placement arithmetic to buy a panel a few pixels back.
 */
export const MAP_SEARCH_LAYOUT = {
  /** The input pill is 376 and the panel 377 (Figma) — the wider of the two. */
  width: 377,
  /** D-01: 40px input + the panel's own ceiling, **395** rather than the pre-full-bleed 438 — the
   *  reservation's bottom edge is kept at the SAME absolute y (494) it always was: `insetBlockStart`
   *  moved down 83px (16→99, `<TopNav>`'s own height) to track where the search box really sits
   *  now, and this shrinks by the same 83px so the zone's total screen-space footprint is unchanged
   *  rather than reaching 83px further into the map than it used to. Reproduced and confirmed via
   *  map-anchored-panel.test.tsx: an unchanged 438 pushed the reservation's bottom edge from 494 to
   *  577, which newly swallowed the "place below the anchor" candidate for an anchor around y≈300
   *  (`placeMapPanel`'s "best fallback" scoring then picked a worse side purely by least-overlap). */
  height: 40 + 395,
  /** D-01: `top: 99px` — Figma's own measurement against the full-bleed frame, which the Map now
   *  is. Not the same number as the legend's 106px; the two clusters do not sit at the same y. */
  insetBlockStart: 99,
  /** `inset-inline-start: 82px`, Figma's own measurement — off the `--space-*` scale. */
  insetInlineStart: 82,
} as const;

/**
 * The regions of the map area a floating panel must not cover (113586 §4f). Consumed by
 * `placeMapPanel`.
 *
 * The buttons and the gaps between them are ONE rect, deliberately.
 */
export function controlExclusionZones(map: { width: number; height: number }): MapRect[] {
  const { buttonSize, buttonGap, buttonCount, groupGap, insetBlockEnd, insetInlineEnd } =
    MAP_CONTROL_LAYOUT;
  // My location, then the zoom pair: one group gap and (count - 2) button gaps.
  const height = buttonCount * buttonSize + groupGap + (buttonCount - 2) * buttonGap;

  return [
    {
      x: Math.max(0, map.width - insetInlineEnd - buttonSize),
      y: Math.max(0, map.height - insetBlockEnd - height),
      width: buttonSize,
      height,
    },
    {
      x: Math.max(0, map.width - MAP_LEGEND_LAYOUT.insetInlineEnd - MAP_LEGEND_LAYOUT.width),
      y: MAP_LEGEND_LAYOUT.insetBlockStart,
      width: MAP_LEGEND_LAYOUT.width,
      height: MAP_LEGEND_LAYOUT.height,
    },
    {
      x: MAP_SEARCH_LAYOUT.insetInlineStart,
      y: MAP_SEARCH_LAYOUT.insetBlockStart,
      width: MAP_SEARCH_LAYOUT.width,
      height: MAP_SEARCH_LAYOUT.height,
    },
  ];
}
